import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import { motion as m, useScroll, useTransform } from "framer-motion";
import portrait from "../assets/profile.png";
import PageHeader from "../components/PageHeader";
import {
  Button,
  Counter,
  Reveal,
  Section,
  SectionHeading,
} from "../components/ui";
import {
  education,
  experience,
  principles,
  site,
  stats,
} from "../data/site";

const Timeline = ({ entries, label }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 85%", "end 60%"],
  });

  return (
    <div ref={ref} className="relative">
      <div className="absolute left-0 top-0 hidden h-full w-px bg-[var(--color-border)] md:block">
        <m.div
          style={{ scaleY: scrollYProgress }}
          className="h-full w-full origin-top bg-azure dark:bg-electric"
        />
      </div>

      <ol className="space-y-0 md:pl-12">
        {entries.map((entry, i) => (
          <Reveal key={`${label}-${i}`} delay={i * 0.05}>
            <li className="relative border-t border-[var(--color-border)] py-10">
              <span className="absolute -left-[3.3rem] top-[2.9rem] hidden h-2 w-2 rounded-full bg-azure dark:bg-electric md:block" />
              <div className="grid gap-5 lg:grid-cols-12 lg:gap-8">
                <p className="t-eyebrow lg:col-span-3">{entry.period}</p>
                <div className="lg:col-span-9">
                  <h3 className="font-display text-2xl leading-tight md:text-[1.75rem]">
                    {entry.role}
                  </h3>
                  <p className="mt-1.5 font-mono text-[11px] uppercase tracking-[0.16em] text-azure dark:text-electric">
                    {entry.org}
                  </p>
                  <p className="mt-4 max-w-prose text-[15px] leading-relaxed text-ink-muted dark:text-slightwhite/70">
                    {entry.summary}
                  </p>
                  <ul className="mt-5 space-y-2">
                    {entry.points.map((p) => (
                      <li
                        key={p}
                        className="flex gap-3 text-sm leading-relaxed text-ink-muted dark:text-slightwhite/65"
                      >
                        <span className="mt-[9px] inline-block h-1 w-1 shrink-0 rounded-full bg-electric" />
                        {p}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </li>
          </Reveal>
        ))}
      </ol>
    </div>
  );
};

const Profile = () => {
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: imgRef,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [-30, 50]);

  return (
    <>
      <Helmet>
        <title>Profile — Jephthah Boateng</title>
        <meta
          name="description"
          content="Senior full-stack engineer building secure, high-performance web systems end-to-end. Experience, education and the rules I work by."
        />
      </Helmet>

      <PageHeader
        eyebrow="Profile"
        title="Systems that hold under load."
        lead="I build secure, high-performance web systems end-to-end — typed APIs, event-driven pipelines and the interfaces on top of them. Seven years of shipping production software, three of them leading the people shipping it."
        meta={[site.role, site.availability, "Since 2021"]}
      />

      {/* Portrait + narrative */}
      <Section tone="default">
        <div className="grid items-start gap-14 lg:grid-cols-12">
          <div ref={imgRef} className="lg:col-span-5">
            <Reveal direction="right">
              <m.figure style={{ y }} className="relative">
                <div className="absolute -inset-3 rounded-lg border border-[var(--color-border)]" />
                <div className="relative overflow-hidden rounded-lg bg-azure-dark shadow-xl">
                  <img
                    src={portrait}
                    alt={site.name}
                    className="aspect-[4/5] w-full object-cover object-top"
                  />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-azure-dark/30 via-transparent to-transparent" />
                </div>
              </m.figure>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <SectionHeading
              eyebrow="In short"
              index={1}
              title="I build things that have to *hold up.*"
            />
            <div className="mt-8 space-y-6 text-[15px] leading-[1.85] text-ink-muted dark:text-slightwhite/70">
              <p>
                I am a senior full-stack engineer. I work end-to-end — Django,
                FastAPI, NestJS and Node on the server, React, Next.js and
                TypeScript on the client, and the AWS, Docker and CI/CD layer
                that carries both. The work I am proudest of is rarely the work
                that is visible.
              </p>
              <p>
                Most of what I have built lives in the category of systems that
                must not be wrong: ad-level attribution pipelines reconciling
                three platforms nightly, an autonomous budget optimiser trusted
                with real money, exchange flows where the bug that matters is
                the one that touches a balance, and a telehealth platform where
                seven different roles each see a different slice of the same
                clinical record.
              </p>
              <p>
                That shapes how I build. I make things provable before I make
                them quick — idempotent writes so a retry is a no-op, audit
                trails so a decision can be reconstructed months later,
                reconciliation jobs that converge on correct rather than
                assuming they never drifted, and permissions enforced on the
                server because a hidden button has never stopped anybody.
              </p>
              <p>
                I have led engineering teams and reviewed the work of external
                partners before it reached production. I write about the craft
                at length, because thinking that cannot survive being written
                down usually was not thinking.
              </p>
            </div>

            <Reveal delay={0.15}>
              <div className="mt-10 flex flex-wrap gap-4">
                <Button to="/projects" withArrow>
                  See the work
                </Button>
                <Button to="/contact" variant="outline" withArrow>
                  Get in touch
                </Button>
              </div>
            </Reveal>
          </div>
        </div>
      </Section>

      {/* Stats */}
      <Section tone="warm" className="!py-16 md:!py-20">
        <div className="grid grid-cols-2 gap-10 md:grid-cols-4">
          {stats.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.07}>
              <div>
                <p className="font-display text-5xl font-semibold leading-none text-azure dark:text-electric md:text-6xl">
                  <Counter value={s.value} suffix={s.suffix} />
                </p>
                <p className="t-eyebrow mt-4">{s.label}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Experience */}
      <Section tone="default">
        <SectionHeading
          eyebrow="Experience"
          index={2}
          title="Where the practice was built."
        />
        <div className="mt-14">
          <Timeline entries={experience} label="exp" />
        </div>
      </Section>

      {/* Education */}
      <Section tone="warm">
        <SectionHeading
          eyebrow="Education"
          index={3}
          title="What I was formally taught."
        />
        <div className="mt-14">
          <Timeline entries={education} label="edu" />
        </div>
      </Section>

      {/* Principles */}
      <section className="relative overflow-hidden bg-gradient-brand py-24 text-paper md:py-32">
        <div className="noise-panel pointer-events-none absolute inset-0 opacity-[0.08]" />
        <div className="relative mx-auto w-full max-w-content px-[var(--gutter)]">
          <Reveal>
            <span className="t-eyebrow text-electric-soft">
              <span className="mr-3 inline-block h-px w-8 bg-electric-soft align-middle" />
              04 — Principles
            </span>
          </Reveal>
          <h2 className="t-title mt-7 max-w-3xl">
            Four rules I do not negotiate on.
          </h2>
          <div className="mt-16 grid gap-10 md:grid-cols-2 lg:gap-14">
            {principles.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.08}>
                <div className="border-t border-white/15 pt-7">
                  <h3 className="font-display text-2xl text-electric md:text-[1.75rem]">
                    {p.title}
                  </h3>
                  <p className="mt-4 max-w-md text-[15px] leading-[1.8] text-paper/75">
                    {p.body}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default Profile;
